import React, { useMemo } from 'react';
import { MapPin, Clock, CheckCircle, TrendingUp } from 'lucide-react';
// Duplicate interfaces to match PackageMap/OpportunitiesPage structure
// In a real app, these would be in a shared types file
interface Property {
  id: number;
  address: string;
  mq: number;
  rooms: number;
  bathrooms: number;
  timeWindow: string;
  tags: string[];
  lat: number;
  lng: number;
}
interface PackageData {
  id: number;
  name: string;
  zoneName: string;
  lat: number;
  lng: number;
  count: number;
  properties: Property[];
  baseEarnings: number;
}
interface PackageHoverTooltipProps {
  pkg: PackageData;
  position: {
    x: number;
    y: number;
  };
  visible: boolean;
}
// Utility to compute bounding circle radius (copied from PackageMap logic)
function computeBoundingCircleRadius(properties: Property[], centerLat: number, centerLng: number): number {
  if (properties.length === 0) return 200;
  const toRad = (deg: number) => deg * Math.PI / 180;
  let maxDist = 0;
  for (const p of properties) {
    const dLat = toRad(p.lat - centerLat);
    const dLng = toRad(p.lng - centerLng);
    const a = Math.sin(dLat / 2) * Math.sin(dLat / 2) + Math.cos(toRad(centerLat)) * Math.cos(toRad(p.lat)) * Math.sin(dLng / 2) * Math.sin(dLng / 2);
    const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    const dist = 6371000 * c; // meters
    if (dist > maxDist) maxDist = dist;
  }
  // Return raw distance for display logic (no padding multiplier)
  return maxDist;
}
export function PackageHoverTooltip({
  pkg,
  position,
  visible
}: PackageHoverTooltipProps) {
  // 1. Calculate Proximity
  const radius = useMemo(() => {
    return computeBoundingCircleRadius(pkg.properties, pkg.lat, pkg.lng);
  }, [pkg]);
  const proximityText = radius < 1000 ? `Proprietà entro ${Math.round(radius)}m` : `Proprietà entro ${(radius / 1000).toFixed(1)} km`;
  // 2. Calculate Time Overlap
  const timeOverlap = useMemo(() => {
    if (pkg.properties.length === 0) return 'Nessun orario';
    let maxStart = 0; // Minutes from midnight
    let minEnd = 24 * 60; // Minutes from midnight
    for (const p of pkg.properties) {
      if (!p.timeWindow) continue;
      const [startStr, endStr] = p.timeWindow.split(' - ');
      if (!startStr || !endStr) continue;
      const [sh, sm] = startStr.split(':').map(Number);
      const [eh, em] = endStr.split(':').map(Number);
      const startMins = sh * 60 + sm;
      const endMins = eh * 60 + em;
      if (startMins > maxStart) maxStart = startMins;
      if (endMins < minEnd) minEnd = endMins;
    }
    if (maxStart < minEnd) {
      const formatTime = (mins: number) => {
        const h = Math.floor(mins / 60);
        const m = mins % 60;
        return `${h.toString().padStart(2, '0')}:${m.toString().padStart(2, '0')}`;
      };
      return `${formatTime(maxStart)} - ${formatTime(minEnd)}`;
    }
    return 'Nessuna sovrapposizione';
  }, [pkg]);
  // 3. Calculate Common Tags
  const commonTags = useMemo(() => {
    const tagCounts: Record<string, number> = {};
    pkg.properties.forEach(p => {
      p.tags.forEach(tag => {
        tagCounts[tag] = (tagCounts[tag] || 0) + 1;
      });
    });
    const common = Object.entries(tagCounts).filter(([_, count]) => count >= 2).map(([tag]) => tag);
    if (common.length > 0) return common.join(', ');
    return 'Servizi diversificati';
  }, [pkg]);
  if (!visible) return null;
  return <div className="absolute z-[1000] pointer-events-none transition-opacity duration-200" style={{
    left: position.x + 20,
    top: position.y,
    transform: 'translateY(-50%)',
    opacity: visible ? 1 : 0
  }} data-id="element-1501">
      <div className="bg-white rounded-xl shadow-2xl border border-gray-200 p-4 w-[280px] relative" data-id="element-1502">
        {/* Arrow pointing left */}
        <div className="absolute top-1/2 -left-2 -translate-y-1/2 w-4 h-4 bg-white border-l border-b border-gray-200 transform rotate-45" style={{
        boxShadow: '-2px 2px 2px -2px rgba(0,0,0,0.05)'
      }} data-id="element-1503"></div>

        {/* Header */}
        <div className="relative z-10" data-id="element-1504">
          <div className="flex justify-between items-start mb-2" data-id="element-1505">
            <div data-id="element-1506">
              <h3 className="font-bold text-navy text-sm mb-1" data-id="element-1507">{pkg.name}</h3>
              <div className="flex items-center gap-2" data-id="element-1508">
                <span className="bg-gray-100 text-gray-500 rounded-full text-[10px] font-medium px-2 py-0.5 border border-gray-200" data-id="element-1509">
                  {pkg.zoneName}
                </span>
                <span className="text-[10px] text-gray-400" data-id="element-1510">
                  {pkg.count} proprietà
                </span>
              </div>
            </div>
            <div className="text-right" data-id="element-1511">
              <div className="flex items-center gap-1 text-teal-deep font-bold text-sm" data-id="element-1512">
                <TrendingUp size={14} data-id="element-1513" />€{pkg.baseEarnings}
              </div>
            </div>
          </div>

          <div className="border-t border-gray-100 my-3" data-id="element-1514"></div>

          {/* Benefits */}
          <div className="space-y-3" data-id="element-1515">
            {/* Proximity */}
            <div className="flex items-start gap-3" data-id="element-1516">
              <div className="bg-teal-50 rounded-lg p-1.5 flex-shrink-0" data-id="element-1517">
                <MapPin size={14} className="text-teal-deep" data-id="element-1518" />
              </div>
              <div data-id="element-1519">
                <p className="text-xs font-medium text-navy" data-id="element-1520">
                  Proprietà ravvicinate
                </p>
                <p className="text-xs text-gray-600" data-id="element-1521">{proximityText}</p>
              </div>
            </div>

            {/* Time Window */}
            <div className="flex items-start gap-3" data-id="element-1522">
              <div className="bg-teal-50 rounded-lg p-1.5 flex-shrink-0" data-id="element-1523">
                <Clock size={14} className="text-teal-deep" data-id="element-1524" />
              </div>
              <div data-id="element-1525">
                <p className="text-xs font-medium text-navy" data-id="element-1526">
                  Finestre compatibili
                </p>
                <p className="text-xs text-gray-600" data-id="element-1527">{timeOverlap}</p>
              </div>
            </div>

            {/* Common Services */}
            <div className="flex items-start gap-3" data-id="element-1528">
              <div className="bg-teal-50 rounded-lg p-1.5 flex-shrink-0" data-id="element-1529">
                <CheckCircle size={14} className="text-teal-deep" data-id="element-1530" />
              </div>
              <div data-id="element-1531">
                <p className="text-xs font-medium text-navy" data-id="element-1532">Servizi simili</p>
                <p className="text-xs text-gray-600 leading-tight" data-id="element-1533">
                  {commonTags}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>;
}